import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const STAFF_ROLES = new Set(['admin', 'manager', 'agreements', 'docs', 'visa', 'cro', 'education']);

const AdminLoginPage = () => {
  const { signIn, user, role, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [formValues, setFormValues] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const redirectPath = useMemo(() => {
    if (location.state && typeof location.state === 'object' && location.state.from) {
      return location.state.from;
    }
    return '/admin';
  }, [location.state]);

  useEffect(() => {
    if (loading) return;
    if (!user) return;

    // Check user's metadata role to determine if they're staff
    const metadataRole =
      user?.app_metadata?.role ||
      user?.user_metadata?.role;

    // If user has a staff role in metadata, they should be in admin
    if (STAFF_ROLES.has(role) || (metadataRole && STAFF_ROLES.has(metadataRole))) {
      navigate(redirectPath, { replace: true });
      return;
    }

    // Only redirect to portal if user is explicitly a client
    if (role === 'client' && (!metadataRole || metadataRole === 'client')) {
      navigate('/portal', { replace: true });
    }
  }, [loading, user, role, navigate, redirectPath]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const normalizeEmail = (raw) => raw.trim().toLowerCase();

  const resolveLoginEmail = (inputEmail) => {
    const normalized = normalizeEmail(inputEmail);
    if (normalized === 'kichukapp@gmail.com') {
      // Supabase project only accepts company domains; map Gmail admin
      return 'kichukapp@ravanaindustries.com';
    }
    return normalized;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const loginEmail = resolveLoginEmail(formValues.email);
      await signIn({
        email: loginEmail,
        password: formValues.password
      });
      navigate(redirectPath, { replace: true });
    } catch (err) {
      let message = err.message || 'Unable to sign in. Please try again.';
      if (normalizeEmail(formValues.email) === 'kichukapp@gmail.com') {
        message = 'Sign-in failed. Please ensure you are using the provided admin password for kichukapp@gmail.com.';
      }
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const isFormDisabled = submitting;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="w-full max-w-md space-y-8 bg-white/95 backdrop-blur rounded-2xl shadow-2xl p-8 border border-slate-100">
        <div className="space-y-2 text-center">
          <Link to="/" className="inline-flex items-center text-sm text-blue-600 hover:text-blue-500 transition-colors">
            <span aria-hidden="true" className="mr-2">←</span>
            Back to site
          </Link>
          <h1 className="text-3xl font-bold text-slate-900">Client Login</h1>
          <p className="text-sm text-slate-500">
            Sign in to access your client portal.
          </p>
        </div>

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 text-red-700 px-4 py-3 text-sm">
            {error}
          </div>
        )}

        <form className="mt-6 space-y-5" onSubmit={handleSubmit}>
          <div className="space-y-2 text-left">
            <label htmlFor="email" className="block text-sm font-medium text-slate-700">
              Email Address
            </label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              value={formValues.email}
              onChange={handleChange}
              disabled={isFormDisabled}
              className="w-full rounded-lg border border-slate-200 px-4 py-2.5 text-slate-900 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:opacity-60"
            />
          </div>

          <div className="space-y-2 text-left">
            <label htmlFor="password" className="block text-sm font-medium text-slate-700">
              Password
            </label>
            <input
              id="password"
              name="password"
              type="password"
              autoComplete="current-password"
              required
              value={formValues.password}
              onChange={handleChange}
              disabled={isFormDisabled}
              className="w-full rounded-lg border border-slate-200 px-4 py-2.5 text-slate-900 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:opacity-60"
            />
          </div>

          <button
            type="submit"
            className="btn btn-accent w-full text-base font-semibold tracking-wide disabled:opacity-60 disabled:cursor-not-allowed"
            disabled={isFormDisabled}
          >
            {submitting ? 'Signing in…' : 'Sign In'}
          </button>
        </form>

        <div className="text-xs text-slate-400 text-center">
          Need assistance? Contact our support team or your immigration consultant.
        </div>
      </div>
    </div>
  );
};

export default AdminLoginPage;
